
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody } from "@/components/ui/table";
import { EtsyOrder } from "@/types/etsy";
import OrdersTableHeader from "@/components/OrdersTableHeader";
import OrdersTableRow from "@/components/OrdersTableRow";
import OrdersTableEmptyStates from "@/components/OrdersTableEmptyStates";
import { useState } from "react";

interface OrdersTableProps {
  orders: EtsyOrder[];
  totalOrders: number;
  isConnected: boolean;
  isFetchingOrders: boolean;
  onOrderUpdate?: (updatedOrder: EtsyOrder) => void;
}

const OrdersTable = ({ orders, totalOrders, isConnected, isFetchingOrders, onOrderUpdate }: OrdersTableProps) => {
  const [expandedOrders, setExpandedOrders] = useState<Set<string>>(new Set());

  const toggleOrderExpansion = (receiptId: string) => {
    const newExpanded = new Set(expandedOrders);
    if (newExpanded.has(receiptId)) {
      newExpanded.delete(receiptId);
    } else {
      newExpanded.add(receiptId);
    }
    setExpandedOrders(newExpanded);
  };

  const getRowCountText = () => {
    if (orders.length === totalOrders) {
      return `Showing ${orders.length} order${orders.length !== 1 ? 's' : ''}`;
    } else {
      return `Showing ${orders.length} of ${totalOrders} order${totalOrders !== 1 ? 's' : ''}`;
    }
  };

  const handleOrderUpdate = (updatedOrder: EtsyOrder) => {
    console.log("[OrdersTable] Forwarding updated order to parent", updatedOrder);
    if (onOrderUpdate) {
      onOrderUpdate(updatedOrder);
    }
  };

  const handleRowClick = (receiptId: string, event: React.MouseEvent) => {
    // Prevent row click when clicking on the action buttons
    if ((event.target as HTMLElement).closest('button')) {
      return;
    }
    toggleOrderExpansion(receiptId);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Order Management</CardTitle>
        {isConnected && totalOrders > 0 && (
          <div className="text-sm text-gray-600">
            {getRowCountText()}
          </div>
        )}
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <Table>
            <OrdersTableHeader />
            <TableBody>
              {orders.map((order, index) => (
                <OrdersTableRow
                  key={`${order.receiptId}-${index}`}
                  order={order}
                  index={index}
                  isExpanded={expandedOrders.has(order.receiptId)}
                  onToggleExpansion={toggleOrderExpansion}
                  onRowClick={handleRowClick}
                  onOrderUpdate={handleOrderUpdate}
                />
              ))}
            </TableBody>
          </Table>
          <OrdersTableEmptyStates 
            isConnected={isConnected}
            hasOrders={orders.length > 0}
            isFetchingOrders={isFetchingOrders}
          />
        </div>
      </CardContent>
    </Card>
  );
};

export default OrdersTable;
